
import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SectionTitle } from "@/components/SectionTitle";
import { CTASection } from "@/components/CTASection";
import { Button } from "@/components/ui/button";
import { TreatmentServiceCard } from "@/components/TreatmentServiceCard";
import { FAQAccordion } from "@/components/FAQAccordion";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { PageTitle } from "@/components/PageTitle";

const SmileMakeover = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // FAQs specific to Smile Makeover
  const faqs = [
    {
      question: "What is a smile makeover?",
      answer: "A smile makeover is a comprehensive cosmetic dental treatment plan that combines multiple procedures to enhance the overall appearance of your smile. At Asrani Dental in Sadar, Nagpur, we design custom smile makeovers addressing issues like tooth color, alignment, spacing, missing teeth, and gum aesthetics to create a harmonious, beautiful smile that complements your facial features."
    },
    {
      question: "What procedures are included in a smile makeover?",
      answer: "A smile makeover at our Nagpur clinic may include various treatments depending on your specific needs and goals: teeth whitening, porcelain veneers, dental crowns, dental implants, tooth bonding, gum contouring, orthodontic treatment, and more. During your consultation, we'll recommend the ideal combination of procedures for your unique situation."
    },
    {
      question: "How long does a smile makeover take?",
      answer: "The duration varies depending on the procedures involved and the complexity of your case. Simple smile makeovers at Asrani Dental in Nagpur might be completed in just 1-2 visits, while comprehensive makeovers involving multiple procedures could take several months. We'll provide a detailed timeline during your consultation."
    },
    {
      question: "Is a smile makeover permanent?",
      answer: "Many smile makeover procedures provide long-lasting results, but some treatments may need maintenance or replacement over time. For example, veneers and crowns typically last 10-15 years, while teeth whitening might need refreshing periodically. At our Nagpur clinic, we'll advise you on how to maximize the longevity of your smile makeover results."
    },
    {
      question: "How do I maintain my smile makeover results?",
      answer: "Maintaining your beautiful new smile from Asrani Dental, Sadar involves regular dental check-ups and cleanings, excellent home oral hygiene, avoiding habits that can damage teeth (like nail biting or using teeth as tools), wearing a nightguard if you grind your teeth, and following any specific care instructions for your particular treatments."
    }
  ];

  // Testimonials for Smile Makeover
  const testimonials = [
    {
      name: "Deepa Sharma",
      comment: "After years of being embarrassed about my smile, I decided to get a smile makeover at Asrani Dental in Nagpur. Dr. Asrani took time to understand exactly what I wanted and created a personalized plan. The transformation is incredible - my confidence has soared, and I can't stop smiling in photos now!",
      rating: 5
    },
    {
      name: "Vikrant Malhotra",
      comment: "My smile makeover at Asrani Dental Clinic in Sadar included veneers, crowns, and whitening. The team was professional throughout the process and made sure I was comfortable every step of the way. The results exceeded my expectations - I finally have the smile I've always wanted.",
      rating: 5
    },
    {
      name: "Meera Patel",
      comment: "Having lived with discolored, uneven teeth for most of my life, I finally decided to do something about it. The smile makeover process at Asrani Dental in Nagpur was so well-planned and executed. Dr. Asrani is truly an artist - he considered my facial features and created a smile that looks completely natural while being absolutely beautiful.",
      rating: 5
    }
  ];

  const handleBookAppointment = () => {
    window.open("https://www.healthcoco.com/nagpur/doctor/dr-hemant-asrani-endodontist-dentist/USRH249D644", "_blank");
  };

  return (
    <div className="min-h-screen">
      <PageTitle title="Smile Makeover in Nagpur | Cost of Smile Makeover in Nagpur" />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-dental-offwhite">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-dental-highlight font-medium mb-4 uppercase tracking-wider text-sm">
              Our Services
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold mb-6">
              Smile Makeover in Nagpur
            </h1>
            <p className="text-lg text-dental-muted">
              Transform your smile with a customized smile makeover at Asrani Dental, Sadar, Nagpur.
            </p>
            <div className="mt-8">
              <Button className="btn-primary" onClick={handleBookAppointment}>
                Book Appointment
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Services Overview */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="/lovable-uploads/smilemakeover.jpg" 
                alt="Smile Makeover at Asrani Dental Clinic, Sadar, Nagpur" 
                className="rounded-xl shadow-soft w-full h-auto"
              />
            </div>
            
            <div>
              <SectionTitle 
                title="Complete Smile Transformation" 
                subtitle="Smile Makeover"
                description="At Asrani Dental in Sadar, Nagpur, our comprehensive smile makeover services combine multiple cosmetic procedures to create the smile of your dreams."
                alignment="left"
              />
              
              <div className="mt-8 space-y-4">
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Personalized Design</h3>
                    <p className="text-dental-muted">Each smile makeover is custom-designed to complement your facial features and reflect your personal style.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Comprehensive Approach</h3>
                    <p className="text-dental-muted">Address multiple cosmetic concerns simultaneously for a harmonious, balanced smile transformation.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Digital Smile Design</h3>
                    <p className="text-dental-muted">Advanced technology allows you to preview your new smile before treatment begins.</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <CheckCircle2 size={24} className="text-dental-highlight mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium mb-1">Life-Changing Results</h3>
                    <p className="text-dental-muted">Experience boosted confidence and self-esteem with a smile you're proud to show off.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Treatments Section */}
      <section className="py-16 md:py-24 bg-dental-cream">
        <div className="container-custom">
          <SectionTitle 
            title="Our Smile Makeover Services" 
            subtitle="Services We Offer"
            description="At Asrani Dental in Sadar, Nagpur, we combine multiple treatments to create your perfect smile."
            alignment="center"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            <TreatmentServiceCard 
              title="Porcelain Veneers"
              description="Ultra-thin porcelain shells bonded to the front of teeth to transform their shape, size, color, and overall appearance."
              imageUrl="/lovable-uploads/79fb2d9d-3034-47e3-82ca-b9eb1d45702f.png"
            />
            
            <TreatmentServiceCard 
              title="Teeth Whitening"
              description="Professional whitening treatments to remove stains and discoloration, brightening your smile by several shades."
              imageUrl="/lovable-uploads/545cfd03-f389-4ab9-ac50-295c248f3cd9.png"
            />
            
            <TreatmentServiceCard 
              title="Dental Crowns"
              description="Custom-made covers that encase the entire visible portion of a tooth, restoring its shape, size, strength, and appearance."
              imageUrl="/lovable-uploads/833b4b48-2be3-4694-94b8-bc2299fb8d86.png"
            />
            
            <TreatmentServiceCard 
              title="Dental Implants"
              description="Permanent replacement for missing teeth that look, feel, and function like natural teeth."
              imageUrl="/lovable-uploads/285554d6-4566-4a60-9073-9f9864390211.png"
            />
            
            <TreatmentServiceCard 
              title="Gum Contouring"
              description="Reshaping the gum line to create a more balanced, aesthetically pleasing smile and correct a 'gummy' appearance."
              imageUrl="/lovable-uploads/7b5dfe8a-e79d-4e59-b614-148e8a422f5a.png"
            />
            
            <TreatmentServiceCard 
              title="Dental Bonding"
              description="Application of tooth-colored resin to repair chips, cracks, gaps, and discoloration for a more uniform appearance."
              imageUrl="/lovable-uploads/49df8a59-4334-4e73-bcb6-d9838e803286.png"
            />
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <SectionTitle 
            title="Patient Testimonials" 
            subtitle="What Our Patients Say"
            description="Hear from our patients about their smile makeover experiences at Asrani Dental in Sadar, Nagpur."
            alignment="center"
          />
          
          <div className="max-w-4xl mx-auto mt-12">
            <ServiceTestimonials testimonials={testimonials} />
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-dental-offwhite">
        <div className="container-custom">
          <SectionTitle 
            title="Frequently Asked Questions" 
            subtitle="Common Questions"
            description="Find answers to common questions about smile makeovers at Asrani Dental, Nagpur."
            alignment="center"
          />
          
          <div className="max-w-3xl mx-auto mt-12">
            <FAQAccordion faqs={faqs} />
          </div>
        </div>
      </section>
      
      {/* Next Steps Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif font-semibold mb-6">Ready to Transform Your Smile?</h2>
            <p className="text-lg text-dental-muted mb-8">
              Contact Asrani Dental in Sadar, Nagpur today to schedule your smile makeover consultation. Our expert team is ready to help you achieve the smile you've always wanted.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button className="btn-primary flex items-center text-base group" onClick={handleBookAppointment}>
                Book Appointment
                <ArrowRight size={18} className="ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
              
              <Button className="btn-secondary flex items-center text-base" onClick={() => window.location.href = '/contact'}>
                Contact Us
              </Button>
            </div>
            
            <div className="mt-10">
              <p className="text-dental-muted mb-4">Interested in our other dental services in Nagpur?</p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/services/cosmetic-dentistry" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Cosmetic Dentistry
                </Link>
                <Link to="/services/tooth-whitening" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Tooth Whitening
                </Link>
                <Link to="/services/dental-implants" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Dental Implants
                </Link>
                <Link to="/services/orthodontics" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Orthodontics
                </Link>
                <Link to="/services/laser-dentistry" className="text-dental-highlight hover:text-dental-accent transition-colors">
                  Laser Dentistry
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <CTASection
        title="Your Dream Smile Awaits"
        description="At Asrani Dental in Sadar, Nagpur, our comprehensive smile makeover services can transform your appearance and boost your confidence. Schedule your consultation today."
        primaryButtonText="Book an Appointment"
        secondaryButtonText="Learn More"
        primaryButtonAction={handleBookAppointment}
        secondaryButtonAction={() => window.location.href = "/about"}
      />
      
      <Footer />
    </div>
  );
};

export default SmileMakeover;
